import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Printer, TrendingUp, TrendingDown, Minus, Loader2, BarChart3 } from "lucide-react";
import { toast } from "sonner";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine, BarChart, Bar } from "recharts";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, errMsg } from "@/lib/api";
import { ProgressSummary } from "@/components/ProgressSummary";

const TEAL = "#0D5C63";
const AMBER = "#D97706";
const axis = { fontSize: 11, fill: "#64748B" };

function Trend({ delta, unit = "pts", goodWhenDown = false }) {
  const Icon = delta > 0 ? TrendingUp : delta < 0 ? TrendingDown : Minus;
  const good = goodWhenDown ? delta < 0 : delta > 0;
  const color = delta === 0 ? "text-slate-500" : good ? "text-emerald-600" : "text-red-600";
  return <span className={`flex items-center gap-1 text-sm font-semibold ${color}`}><Icon className="h-4 w-4" />{delta > 0 ? "+" : ""}{delta} {unit}</span>;
}

function GoalChart({ g, i }) {
  const pts = g.points.map((p, k) => ({ ...p, label: `${p.date.slice(5)}${g.points.filter((x) => x.date === p.date).length > 1 ? ` #${k + 1}` : ""}` }));
  const first = pts[0].pct;
  const last = pts[pts.length - 1].pct;
  const avg = Math.round(pts.reduce((s, p) => s + p.pct, 0) / pts.length);
  return (
    <div className="rounded-xl border bg-white p-5 break-inside-avoid" data-testid={`goal-chart-${i}`}>
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div>
          <h3 className="font-semibold text-slate-900">{g.name}</h3>
          <p className="text-xs text-slate-500">{pts.length} session{pts.length > 1 ? "s" : ""} · avg {avg}% · {first}% → {last}%</p>
        </div>
        <div className="flex items-center gap-3">
          {last >= 80 && <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700">At mastery criterion</span>}
          <Trend delta={last - first} />
        </div>
      </div>
      <div className="h-52">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={pts} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
            <XAxis dataKey="label" tick={axis} />
            <YAxis domain={[0, 100]} tick={axis} unit="%" />
            <Tooltip formatter={(v, n, p) => [`${v}% (${p.payload.correct}/${p.payload.total}${p.payload.prompt_level ? `, ${p.payload.prompt_level}` : ""})`, "Accuracy"]} />
            <ReferenceLine y={80} stroke={AMBER} strokeDasharray="4 4" label={{ value: "80% mastery", position: "insideTopRight", fontSize: 10, fill: AMBER }} />
            <Line type="monotone" dataKey="pct" stroke={TEAL} strokeWidth={2.5} dot={{ r: 4, fill: TEAL }} isAnimationActive={false} />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function BehaviorChart({ b, i }) {
  const pts = b.points.map((p) => ({ ...p, label: p.date.slice(5) }));
  const total = pts.reduce((s, p) => s + p.frequency, 0);
  return (
    <div className="rounded-xl border bg-white p-5 break-inside-avoid" data-testid={`behavior-chart-${i}`}>
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="font-semibold text-slate-900">{b.name}</h3>
          <p className="text-xs text-slate-500">{total} total · avg {(total / pts.length).toFixed(1)} per session</p>
        </div>
        <Trend delta={pts[pts.length - 1].frequency - pts[0].frequency} unit="per session" goodWhenDown />
      </div>
      <div className="h-44">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={pts} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
            <XAxis dataKey="label" tick={axis} />
            <YAxis allowDecimals={false} tick={axis} />
            <Tooltip formatter={(v) => [v, "Frequency"]} />
            <Bar dataKey="frequency" fill={AMBER} radius={[4, 4, 0, 0]} isAnimationActive={false} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export default function ProgressPage() {
  const { id } = useParams();
  const [range, setRange] = useState({ start: "", end: "" });
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get(`/clients/${id}/progress`, { params: range }).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [id, range]);

  if (!data) return <div className="grid place-items-center h-64"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;
  const { client, goals, behaviors, session_count } = data;

  return (
    <div className="space-y-8 fade-up">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <Link to="/clients" className="no-print text-sm text-slate-500 hover:text-primary flex items-center gap-1 mb-2" data-testid="progress-back-link"><ArrowLeft className="h-3.5 w-3.5" />Clients</Link>
          <div className="text-xs uppercase tracking-[0.18em] text-amber-600">Progress report</div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight" data-testid="progress-client-name">{client.name}</h1>
          <p className="text-slate-500 mt-1 text-sm">
            {client.diagnosis && `${client.diagnosis} · `}{session_count} session note{session_count === 1 ? "" : "s"}
            {(range.start || range.end) && ` · ${range.start || "start"} to ${range.end || "today"}`}
          </p>
        </div>
        <div className="no-print flex flex-wrap items-end gap-3">
          <div className="space-y-1"><Label className="text-xs">From</Label><Input type="date" value={range.start} onChange={(e) => setRange({ ...range, start: e.target.value })} className="h-10 bg-white" data-testid="progress-start-input" /></div>
          <div className="space-y-1"><Label className="text-xs">To</Label><Input type="date" value={range.end} onChange={(e) => setRange({ ...range, end: e.target.value })} className="h-10 bg-white" data-testid="progress-end-input" /></div>
          <Button className="rounded-full h-10" onClick={() => window.print()} data-testid="progress-print-button"><Printer className="h-4 w-4 mr-2" />Print / Save PDF</Button>
        </div>
      </div>

      {goals.length === 0 && behaviors.length === 0 ? (
        <div className="rounded-2xl border border-dashed bg-white p-12 text-center" data-testid="progress-empty-state">
          <BarChart3 className="h-8 w-8 mx-auto text-primary/60" />
          <h3 className="mt-4 text-lg font-semibold">No trial data yet</h3>
          <p className="text-sm text-slate-500 mt-1">Log trials in this client's session notes and their progress will chart here.</p>
        </div>
      ) : (
        <>
          <ProgressSummary clientId={id} range={range} />
          {goals.length > 0 && (
            <section className="space-y-4">
              <h2 className="text-lg font-semibold">Skill acquisition · trial accuracy</h2>
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">{goals.map((g, i) => <GoalChart key={i} g={g} i={i} />)}</div>
            </section>
          )}
          {behaviors.length > 0 && (
            <section className="space-y-4">
              <h2 className="text-lg font-semibold">Behavior reduction · frequency per session</h2>
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">{behaviors.map((b, i) => <BehaviorChart key={i} b={b} i={i} />)}</div>
            </section>
          )}
        </>
      )}
    </div>
  );
}
